import {
  calculatePositionSnap,
  calculateResizeSnap,
  getElementContainerRelativeRect,
  type DragOperation,
  type ElementRect,
  type SnapGuide,
  type SnapResult,
} from "@skilitics/drag-drop";

import { DND_CANVAS_EDITOR_ID } from "../dnd/dndConstants.ts";
import { draggableDataSchema } from "../dnd/dndSchemas.ts";
import { isResizeableId, parseResizeableId } from "../dnd/dndUtils.ts";
import { getNodeElement, NODE_ELEMENT_ID_SELECTOR, parseNodeElementId } from "../element-state/utils.ts";
import { isNodeResizeId, parseNodeResizeHandle } from "./useNodeResizeMonitor.ts";

/** Returns the rect of an element in editor-relative screen coordinates. */
export const getEditorRelativeRect = (element: HTMLElement): ElementRect =>
  getElementContainerRelativeRect(element, document.getElementById(DND_CANVAS_EDITOR_ID));

/**
 * Collects the edges of sibling elements, parent container, and canvas root container
 * as potential snap targets, in editor-relative screen coordinates.
 */
export const collectSnapTargetEdges = (nodeId: number): SnapGuide[] => {
  const nodeElement = getNodeElement(nodeId);
  if (!nodeElement) return [];

  const parentContainer = nodeElement.parentElement?.closest<HTMLElement>(NODE_ELEMENT_ID_SELECTOR);
  if (!parentContainer) return [];

  const edges: SnapGuide[] = [];
  const pushRectEdges = (rect: ElementRect) => {
    edges.push(
      { position: rect.top, orientation: "horizontal" },
      { position: rect.top + rect.height, orientation: "horizontal" },
      { position: rect.left, orientation: "vertical" },
      { position: rect.left + rect.width, orientation: "vertical" },
    );
  };

  pushRectEdges(getEditorRelativeRect(parentContainer));

  const editorContainer = document.getElementById(DND_CANVAS_EDITOR_ID);
  const canvasRootContainer = editorContainer?.querySelector<HTMLElement>(NODE_ELEMENT_ID_SELECTOR);
  if (canvasRootContainer && canvasRootContainer !== parentContainer && canvasRootContainer !== nodeElement) {
    pushRectEdges(getEditorRelativeRect(canvasRootContainer));
  }

  const siblingNodes = parentContainer.querySelectorAll<HTMLElement>(`:scope > ${NODE_ELEMENT_ID_SELECTOR}`);
  for (const sibling of siblingNodes) {
    const siblingId = parseNodeElementId(sibling.id);
    if (siblingId === undefined || siblingId === nodeId) continue;
    pushRectEdges(getEditorRelativeRect(sibling));
  }

  return edges;
};

/**
 * Top-level snap orchestrator for the canvas. Inspects the drag source to
 * decide whether it's a resize-handle drag or an absolute-position drag, then
 * delegates to the appropriate pure snap calculation. Returns `undefined`
 * when the drag is not a candidate for snapping.
 */
export const calculateCanvasSnap = (operation: DragOperation): SnapResult | undefined => {
  const source = operation.source;
  if (!source) return undefined;

  if (isResizeableId(source.id)) {
    if (!isNodeResizeId(source.id)) return undefined;

    const { id, layoutableId: nodeId } = parseResizeableId(source.id);
    const handle = parseNodeResizeHandle(id);
    if (!handle) return undefined;

    const nodeElement = getNodeElement(nodeId);
    if (!nodeElement) return undefined;

    const nodeRect = getEditorRelativeRect(nodeElement);
    const snapTargets = collectSnapTargetEdges(nodeId);
    const { delta, snapGuides } = calculateResizeSnap(nodeRect, handle, operation.transform, snapTargets);

    return { transform: { x: delta.x, y: delta.y }, guides: snapGuides };
  }

  const parsedData = draggableDataSchema.safeParse(source.data);
  if (!parsedData.success) return undefined;
  if (!parsedData.data.isAbsolutelyPositioned || parsedData.data.inLayersPanel) return undefined;
  if (parsedData.data.node.type !== "instance") return undefined;

  const nodeId = parsedData.data.node.id;
  const nodeElement = getNodeElement(nodeId);
  if (!nodeElement) return undefined;

  const nodeRect = getEditorRelativeRect(nodeElement);
  const snapTargets = collectSnapTargetEdges(nodeId);
  const { delta, snapGuides } = calculatePositionSnap(nodeRect, operation.transform, snapTargets);

  return { transform: { x: delta.x, y: delta.y }, guides: snapGuides };
};
